import { User } from 'oidc-client-ts';

export const ROLES = {
  FACILITATOR_PORTAL: 'Facilitator.Portal',
  ADMIN: 'Admin'
} as const;

/**
 * Safely extracts user roles from OIDC profile claims.
 * Handles string, array of strings, or standard Microsoft role claim URI.
 */
export const getUserRoles = (user: User | null | undefined): string[] => {
  if (!user?.profile) {
    return [];
  }

  const profile = user.profile as Record<string, any>;
  const rawRole =
    profile.role ??
    profile.roles ??
    profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

  if (!rawRole) {
    return [];
  }

  if (Array.isArray(rawRole)) {
    return rawRole.map((r) => String(r).trim()).filter(Boolean);
  }

  if (typeof rawRole === 'string') {
    return [rawRole.trim()].filter(Boolean);
  }

  return [];
};

/**
 * Checks whether the user has at least one of the required roles.
 */
export const hasRequiredRole = (
  user: User | null | undefined,
  allowedRoles?: string[]
): boolean => {
  if (!allowedRoles || allowedRoles.length === 0) {
    return true;
  }

  const userRoles = getUserRoles(user);
  return allowedRoles.some((allowed) => userRoles.includes(allowed));
};

/**
 * Safely extracts user organisation ID from OIDC profile claims (catalyst_org_id).
 */
export const getUserOrganisationId = (
  user: User | null | undefined
): string | null => {
  if (!user?.profile) {
    return null;
  }

  const profile = user.profile as Record<string, any>;
  const orgId = profile.catalyst_org_id;

  if (orgId === undefined || orgId === null || orgId === '') {
    return null;
  }

  return String(orgId).trim();
};

/**
 * Checks whether the user can access the game:
 * 1. Users with 'Admin' role can access any game (bypass).
 * 2. Non-admin users can only access the game if game's organisationId matches user's catalyst_org_id.
 * 3. If game has no organisationId (legacy null), non-admin access is denied.
 */
export const canUserAccessGame = (
  user: User | null | undefined,
  gameOrgId: number | string | null | undefined
): boolean => {
  // Admin role bypasses organisation check
  if (hasRequiredRole(user, [ROLES.ADMIN])) {
    return true;
  }

  // Games without organisationId are denied for non-admin
  if (gameOrgId === null || gameOrgId === undefined || gameOrgId === '') {
    return false;
  }

  const userOrgId = getUserOrganisationId(user);
  if (!userOrgId) {
    return false;
  }

  return String(userOrgId) === String(gameOrgId).trim();
};

